import React, { useEffect, useRef } from 'react';
import './Timeline.css';

interface TimelineEvent {
  year: string;
  title: string;
  description: string;
  image?: string;
  type: 'achievement' | 'exhibition' | 'community' | 'recognition';
}

const Timeline: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);

  const timelineEvents: TimelineEvent[] = [
    {
      year: "1980s",
      title: "Artistic Journey Begins",
      description: "Hector Díaz establishes himself as a promising artist, developing his distinctive style that would later define the 'Art of the Future' aesthetic.",
      type: "achievement"
    },
    {
      year: "1990s",
      title: "Downtown Gallery Opening",
      description: "Opens his gallery in downtown Tacoma, creating a cultural hub that would become central to the city's artistic renaissance.",
      image: "/images/newsclippings/IMG_1206.png",
      type: "achievement"
    },
    {
      year: "1996",
      title: "Media Recognition",
      description: "Featured in Sound Life magazine, bringing broader attention to his work and Tacoma's emerging art scene.",
      image: "/images/newsclippings/IMG_1203.png",
      type: "recognition"
    },
    {
      year: "Late 1990s",
      title: "New DAY Program Launch",
      description: "Establishes 'New DAY - Diaz Art for Youth', a groundbreaking program mentoring young artists and providing them with opportunities to develop their talents.",
      type: "community"
    },
    {
      year: "2000s",
      title: "Community Impact",
      description: "His gallery becomes a cornerstone of downtown revitalization efforts, as noted in local press coverage highlighting his positive impact on the community.",
      image: "/images/newsclippings/IMG_1204.png",
      type: "community"
    },
    {
      year: "2000s",
      title: "City of Destiny Award",
      description: "Receives the prestigious City of Destiny Award in recognition of his outstanding contributions to Tacoma's cultural landscape and youth development.",
      type: "recognition"
    },
    {
      year: "2000s",
      title: "Educational Impact",
      description: "Dr. Sharon McGavick and other educational leaders recognize the profound impact of his youth art programs on the community.",
      type: "community"
    },
    {
      year: "Present",
      title: "Lasting Legacy",
      description: "Hector's influence continues to inspire artists and art lovers, with his 'Art of the Future' vision serving as a beacon for creative expression.",
      type: "achievement"
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll('.timeline-item');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const getEventIcon = (type: string) => {
    switch (type) {
      case 'achievement': return '🎨';
      case 'exhibition': return '🖼️';
      case 'community': return '🤝';
      case 'recognition': return '🏆';
      default: return '✨';
    }
  };

  return (
    <section className="timeline-section" id="timeline" ref={sectionRef}>
      <div className="timeline-container">
        <div className="timeline-header hector-fade-in-up">
          <h2 className="hector-section-title">Journey Through Time</h2>
          <p className="timeline-subtitle">Milestones in the life and career of Hector Díaz</p>
          <div className="hector-horizon-line"></div>
        </div>

        <div className="timeline">
          <div className="timeline-line"></div>
          {timelineEvents.map((event, index) => (
            <div
              key={index}
              className={`timeline-item ${index % 2 === 0 ? 'timeline-left' : 'timeline-right'}`}
              style={{ animationDelay: `${index * 0.2}s` }}
            >
              <div className="timeline-marker">
                <span className="timeline-icon">{getEventIcon(event.type)}</span>
                <div className="timeline-year">{event.year}</div>
              </div>

              <div className="timeline-content">
                <div className={`hector-card timeline-card ${event.type}`}>
                  <h3 className="hector-card-title">{event.title}</h3>
                  <p>{event.description}</p>
                  {event.image && (
                    <div className="timeline-image">
                      <img src={event.image} alt={event.title} />
                    </div>
                  )}
                  <div className="timeline-badge">
                    <span className={`badge ${event.type}`}>
                      {event.type.charAt(0).toUpperCase() + event.type.slice(1)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Timeline;